import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { FileSearch, Calendar, Tag, Users, Globe, Activity } from 'lucide-react';
import { useAuditStats } from '@/hooks/useAuditLogs';
import { UserTypeFilter } from '@/hooks/usePaginatedMediaData';

interface AuditStatsProps {
  userTypeFilter?: UserTypeFilter;
}

export const AuditStats = ({ userTypeFilter = 'all' }: AuditStatsProps) => {
  const { data: stats, isLoading } = useAuditStats(userTypeFilter);

  const statCards = [
    {
      title: 'Total de Logs',
      value: stats?.total || 0,
      icon: FileSearch,
      color: 'text-primary',
    },
    {
      title: 'Eventos Hoje',
      value: stats?.today || 0,
      icon: Calendar,
      color: 'text-green-500',
    },
    {
      title: 'Tipos de Eventos',
      value: stats?.uniqueTypes || 0,
      icon: Tag,
      color: 'text-blue-500',
    },
    {
      title: 'Usuários Ativos Hoje',
      value: stats?.activeUsers || 0,
      icon: Users,
      color: 'text-purple-500',
    },
    {
      title: 'IPs Únicos',
      value: stats?.uniqueIps || 0,
      icon: Globe,
      color: 'text-orange-500',
    },
    {
      title: 'Status',
      value: 'Online',
      icon: Activity,
      color: 'text-emerald-500',
    },
  ];

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <Card key={i} className="bg-white/5 border-white/10 animate-pulse">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <Skeleton className="h-4 w-24 bg-white/10" />
              <Skeleton className="h-4 w-4 bg-white/10" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-16 bg-white/10" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {statCards.map((stat, index) => (
        <Card key={index} className="bg-white/5 border-white/10 hover:bg-white/10 transition-all duration-300">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-white/60">{stat.title}</CardTitle>
            <stat.icon className={`h-4 w-4 ${stat.color}`} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">{stat.value.toLocaleString()}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
